import { ScanDetails } from '../../../../../shared/src/data/scanDetails'
import {
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
} from '@material-ui/core'
import React, { useCallback, useMemo } from 'react'
import { ProjectDetails } from '../../../../../shared/src/data/projectDetails'
import { format } from 'date-fns'

type ScanSelectionProps = {
  scans: ScanDetails[]
  selectedScan?: ScanDetails
  setSelectedScan: (scan?: ScanDetails) => void
}

export default function ScanSelection({
  scans,
  selectedScan,
  setSelectedScan,
}: ScanSelectionProps): JSX.Element {
  const projects = useMemo<ProjectDetails[]>(() => {
    const allProjects = scans.map((s) => s.project)
    const uniqueProjectsIds = new Set(allProjects.map((p) => p.id))
    return Array.from(uniqueProjectsIds).map(
      (id) => allProjects.find((p) => p.id === id)!,
    )
  }, [scans])

  const getSortedScanAsProject = useCallback(
    (projectId?: string) =>
      !projectId
        ? []
        : scans
            .filter((s) => s.project.id === projectId)
            .sort(
              (s1, s2) => s2.creationTime.getTime() - s1.creationTime.getTime(),
            ),
    [scans],
  )

  const sortedScanFromTheSameProjectAsSelectedScan = useMemo<ScanDetails[]>(
    () => getSortedScanAsProject(selectedScan?.project.id),
    [selectedScan, getSortedScanAsProject],
  )

  const setSelectedScanByProjectId = useCallback(
    (projectId: string) => {
      const [latestScanForProject] = getSortedScanAsProject(projectId)
      setSelectedScan(latestScanForProject)
    },
    [setSelectedScan, getSortedScanAsProject],
  )

  const setSelectedScanByScanId = useCallback(
    (scanId: string) => {
      setSelectedScan(scans.find((s) => s.id === scanId))
    },
    [scans, setSelectedScan],
  )

  return (
    <>
      <FormControl fullWidth color='secondary' disabled={!scans?.length}>
        <InputLabel>Project</InputLabel>
        <Select
          value={selectedScan?.project.id || ''}
          fullWidth
          onChange={(e) => setSelectedScanByProjectId(e.target.value as string)}
        >
          {projects.map((project) => (
            <MenuItem key={project.id} value={project.id}>
              <ListItemText
                primary={project.name}
                secondary={`Id: ${project.id}`}
              />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl fullWidth color='secondary' disabled={!selectedScan}>
        <InputLabel>Branch</InputLabel>
        <Select
          value={selectedScan?.id || ''}
          fullWidth
          onChange={(e) => setSelectedScanByScanId(e.target.value as string)}
        >
          {sortedScanFromTheSameProjectAsSelectedScan.map((scan, i) => (
            <MenuItem key={scan.id} value={scan.id}>
              <ListItemText
                primary={`Version: ${sortedScanFromTheSameProjectAsSelectedScan.length - i}, 
								Creation Time: ${format(scan.creationTime, 'dd/MM/yyyy, HH:mm')}`}
                secondary={`Id: ${scan.id}`}
              />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </>
  )
}
